// src/features/macro_keywords/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";

// Import the select options defined in filter.tsx
import { macroTypeOptions } from "./filter";

const client = "ia-quantilia";
const table_name = "macro_keywords";

// Zod schema: following the project pattern for enum fields
const FormSchema = z.object({
  name: z.string().nullable().optional(),
  macro_type: z.string().nullable().optional(),
  time_serie_id: z.coerce.number().optional().nullable(),
});

export function MacroKeywordNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Macro Keyword
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Name and Macro Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="macro_type"
                    label={t(`${table_name}.columns.macro_type`, {
                      ns: "tables",
                      defaultValue: "Macro Type",
                    })}
                    options={macroTypeOptions}
                    control={form.control}
                    isRequired
                  />
                </div>

                {/* Row 2: Time Series ID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="time_serie_id"
                    label={t(`${table_name}.columns.time_serie_id`, {
                      ns: "tables",
                      defaultValue: "Time Series ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new record */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel without saving */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
